import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion'; // Importa motion e AnimatePresence
import styles from './Tabs.module.css';

export interface TabItem { // Aggiunto export
  label: string;
  icon?: React.ReactNode; // Aggiunta prop icona opzionale
  content: React.ReactNode;
  disabled?: boolean;
}

interface TabsProps {
  tabs: TabItem[];
  defaultActiveTabIndex?: number;
  variant?: 'primary' | 'secondary'; // Aggiunta prop variant
  onTabChange?: (index: number) => void; // Aggiunta callback per il cambio tab
}

const Tabs: React.FC<TabsProps> = ({ tabs, defaultActiveTabIndex = 0, variant = 'primary', onTabChange }) => {
  const [activeTabIndex, setActiveTabIndex] = useState(defaultActiveTabIndex);

  const handleTabClick = (index: number) => {
    if (!tabs[index].disabled) {
      setActiveTabIndex(index);
      if (onTabChange) {
        onTabChange(index);
      }
    }
  };

  const variantClass = variant === 'secondary' ? styles.secondary : styles.primary;

  return (
    <div className={`${styles.tabsContainer} ${variantClass}`}>
      <div className={styles.tabList} role="tablist">
        {tabs.map((tab, index) => (
          <button
            key={tab.label}
            role="tab"
            aria-selected={activeTabIndex === index}
            aria-controls={`tabpanel-${index}`} // L'ID del pannello rimane lo stesso
            id={`tab-${index}`}
            className={`${styles.tabButton} ${
              activeTabIndex === index ? styles.active : ''
            } ${tab.disabled ? styles.disabled : ''}`}
            onClick={() => handleTabClick(index)}
            disabled={tab.disabled}
          >
            {tab.icon && <span className={styles.tabIcon}>{tab.icon}</span>}
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        ))}
      </div>
      <AnimatePresence initial={false} mode="wait">
        {tabs.map((tab, index) => {
          if (index !== activeTabIndex) return null; // Mostra solo il pannello della tab attiva

          return (
            <motion.div
              key={tab.label + '-' + index} // Chiave univoca per AnimatePresence, combinando label e index
              role="tabpanel"
              id={`tabpanel-${index}`} // ID per accessibilità
              aria-labelledby={`tab-${index}`} // Collegamento alla tab per accessibilità
              className={styles.tabPanel} // Mantieni le tue classi esistenti
              initial={{ opacity: 0, x: 10 }} // Leggero slide da destra
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }} // Leggero slide a sinistra
              transition={{ duration: 0.2 }} // Durata breve
            >
              {tab.content} {/* Contenuto del pannello */}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default Tabs;